import { useEffect } from 'react';
import { unstable_usePrompt } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import {
    GetOrganizationQuery,
    OrganizationPaymentTimeType,
    OrganizationPaymentType,
    useUpdateOrganizationMutation,
    useGetTotemsOfOrganizationQuery,
    GetMicrostoreColumnsOfOrganizationQuery,
    OrganizationInvoiceSettingInput,
} from 'data/__generated__';

import { ActionsAndSave, EntityPageBreadcrumb, PageActions, PageHeader } from 'components/PageHeader';
import { getTotemFullName } from 'helpers/totemLabel';
import { GeneralInfoSection } from './GeneralInfoSection';
import { PaymentInfoSection } from './PaymentInfoSection';
import { SectionContainer, SectionColumn } from 'components/DetailsView/Section';
import { InvoicingDetails } from './InvoicingDetails';
import { ConsumptionConfigurationDetails } from './InvoiceConfigurationDetails/ConsumptionConfigurationDetails';
import { RmeConfigurationDetails } from './InvoiceConfigurationDetails/RmeConfigurationDetails';
import { TotemsDetails } from './TotemsDetails';
import { SitesDetails } from './SitesDetails';
import { UsersDetails } from './UsersDetails';
import { OrdersDetails } from './OrdersDetails';
import { AuditLogs } from 'components/AuditLogs';

type FormData = {
    attachUsersManually: boolean;
    B2BPriceRange?: string | null;
    domains: { value: string }[];
    hasDetailedOrderInvoicing: boolean;
    hasFreefoodIncluded: boolean;
    hasSubscription: boolean;
    maxUsers: number;
    paymentType: OrganizationPaymentType;
    paymentTimeType: OrganizationPaymentTimeType;
    customerType: string | null;
    hasOdooOrganizationParentInvoicing: boolean;
    hasPrepaidOrganizationCards: boolean;
    hasPrepaidSubventions: boolean;
    salesId?: string | null;
    segregatedInvoicing: boolean;
    totalCoworkers?: number | null;
    restrictedProductArrangementId?: string | null;
    odooInvoiceReference: string | null;
    invoiceSettings: OrganizationInvoiceSettingInput;
};

export const OrganizationGlobalForm = ({
    organization,

    microstoreColumns,
}: {
    organization: GetOrganizationQuery['organization'];
    microstoreColumns: GetMicrostoreColumnsOfOrganizationQuery['microstoreColumnsOfOrganization'];
}) => {
    const { _id: organizationId, name, domains } = organization;
    const [updateOrganization, { loading }] = useUpdateOrganizationMutation();
    const { data: totemsData, loading: totemsLoading } = useGetTotemsOfOrganizationQuery({
        variables: { organizationId },
    });

    const methods = useForm<FormData>({
        defaultValues: {
            domains: domains?.map((domain) => {
                return { value: domain };
            }),
        },
    });

    const {
        handleSubmit,
        formState: { isDirty, isSubmitSuccessful },
        reset,
    } = methods;

    unstable_usePrompt({
        message: 'Êtes-vous sûr.e de vouloir quitter cette page sans enregistrer ?',
        when: ({ currentLocation, nextLocation }) => isDirty && currentLocation.pathname !== nextLocation.pathname,
    });

    useEffect(() => {
        if (isSubmitSuccessful) {
            reset({}, { keepValues: true });
        }
    }, [isSubmitSuccessful, reset]);

    useEffect(() => {
        if (organization) {
            reset({
                attachUsersManually: organization.attachUsersManually,
                B2BPriceRange: organization.B2BPriceRange,
                domains: organization.domains?.map((domain) => ({ value: domain })),
                hasDetailedOrderInvoicing: organization.hasDetailedOrderInvoicing,
                hasFreefoodIncluded: organization.hasFreefoodIncluded,
                hasSubscription: organization.hasSubscription,
                maxUsers: organization.maxUsers,
                paymentType: organization.paymentType,
                customerType: organization.customerType,
                hasPrepaidOrganizationCards: organization.hasPrepaidOrganizationCards,
                hasPrepaidSubventions: organization.hasPrepaidSubventions,
                salesId: organization.sales?._id,
                segregatedInvoicing: organization.segregatedInvoicing,
                totalCoworkers: organization.totalCoworkers,
                restrictedProductArrangementId: organization.restrictedProductArrangementId,
                odooInvoiceReference: organization.odooInvoiceReference,
                hasOdooOrganizationParentInvoicing: organization.hasOdooOrganizationParentInvoicing,
                paymentTimeType: organization.paymentTimeType,
                invoiceSettings: {
                    organizationId: organizationId,
                    consumptionColumns: organization.invoiceSettings.consumptionColumns,
                    rmeColumns: organization.invoiceSettings.rmeColumns,
                    state: organization.invoiceSettings.state,
                },
            });
        }
    }, [organization, reset]);
    const onSubmit = handleSubmit(async (fields) => {
        const { ...organizationFields } = fields;

        const formattedDomains = fields.domains.map((domain) => {
            return domain.value;
        });

        if (fields.hasSubscription !== organization.hasSubscription) {
            alert(
                "Attention : vous avez modifié l'abonnement livraisons illimitées, vous devez également le mettre à jour sur Stripe manuellement",
            );
            const newWindow = window.open(
                `https://dashboard.stripe.com/customers/${organization.stripeId}`,
                '_blank',
                'noopener,noreferrer',
            );
            if (newWindow) {
                newWindow.opener = null;
            }
        }

        const { data } = await updateOrganization({
            variables: {
                organizationId,
                fields: {
                    ...organizationFields,
                    domains: formattedDomains,
                },
            },
        });

        if (data) {
            const {
                updateOrganization: { error, formErrors, updatedOrganization },
            } = data;
            if (updatedOrganization) {
                toast.success(`L'organisation "${name}" a bien été modifiée !`);
            } else if (formErrors) {
                formErrors.forEach(({ sectionName, sectionErrors }, index) => {
                    toast.error(
                        <span key={index}>
                            Erreur dans la section "{sectionName}" :
                            {sectionErrors.map(({ fieldName, fieldError }, index) => (
                                <span key={index}>
                                    <br />- {fieldName} : "{fieldError}"
                                </span>
                            ))}
                        </span>,
                        { autoClose: false },
                    );
                });

                if (formErrors.length > 1) {
                    toast.info('Cliquez pour fermer toutes les notifications', {
                        autoClose: false,
                        onClick: () => toast.dismiss(),
                    });
                }
            } else if (error) {
                toast.error(error);
            } else {
                throw Error("Une erreur inconnue s'est produite");
            }
        }
    });
    return (
        <>
            <Helmet title={`${name} · TOTADMIN`} />
            <PageHeader>
                <EntityPageBreadcrumb
                    organization={organization}
                    organizationTotemsDropdown={{
                        isLoading: totemsLoading,
                        items: (totemsData?.totemsOfOrganization ?? []).map((totem) => ({
                            label: getTotemFullName(totem),
                            to: `/totem/${totem._id}`,
                        })),
                        emptyLabel: 'Aucun totem',
                    }}
                />
                <PageActions>
                    <ActionsAndSave
                        actionGroups={[]}
                        onSave={onSubmit}
                        saveDisabled={!isDirty}
                        saveLoading={loading}
                        saveTitle="Mettre à jour"
                    />
                </PageActions>
            </PageHeader>
            <Content>
                <FormProvider {...methods}>
                    <Form onSubmit={onSubmit}>
                        <ScrollableContent>
                            <GeneralInfoSection organization={organization} />
                            <SectionContainer title="Totems" isInitiallyOpen>
                                <TotemsDetails />
                            </SectionContainer>
                            <SectionContainer title="Sites" isInitiallyOpen>
                                <SitesDetails />
                            </SectionContainer>
                            <SectionContainer title="Utilisateurs" isInitiallyOpen>
                                <UsersDetails />
                            </SectionContainer>
                            <SectionContainer title="Commandes récentes" isInitiallyOpen>
                                <OrdersDetails />
                            </SectionContainer>
                            <PaymentInfoSection organization={organization} />
                            <SectionContainer title="Configuration facturation" isInitiallyOpen>
                                <ResponsiveColumnContainer>
                                    <FirstColumn>
                                        <ConsumptionConfigurationDetails
                                            microstoreColumns={microstoreColumns}
                                            B2BPriceRangeId={organization.B2BPriceRange}
                                        />
                                    </FirstColumn>
                                    <SecondColumn>
                                        <RmeConfigurationDetails microstoreColumns={microstoreColumns} />
                                    </SecondColumn>
                                </ResponsiveColumnContainer>
                            </SectionContainer>
                            <SectionContainer title="Facturation" isInitiallyOpen>
                                <InvoicingDetails organizationId={organizationId} organization={organization} />
                            </SectionContainer>
                            <SectionContainer
                                title="Historique des modifications (organisation)"
                                isInitiallyOpen={false}
                            >
                                <AuditLogs collectionDocumentId={organizationId} />
                            </SectionContainer>
                        </ScrollableContent>
                    </Form>
                </FormProvider>
            </Content>
        </>
    );
};

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;

const ResponsiveColumnContainer = styled.div`
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
`;

const FirstColumn = styled(SectionColumn)`
    width: calc(57% - 10px);

    @media (max-width: 2100px) {
        width: 100%;
    }
`;

const SecondColumn = styled(SectionColumn)`
    width: calc(43% - 10px);

    @media (max-width: 2100px) {
        width: 100%;
    }
`;
